import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import './InstructorNotification.css';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { useSocket } from '../../context/SocketContext';
import { useNotifications } from '../../context/NotificationContext';


const InstructorNotificationPage = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { socket } = useSocket();

  // Wrap fetchNotifications in useCallback
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!token) {
        setError('No auth token found.');
        return;
      }

      // Fetch test video notifications
      const videoRes = await axios.get(api.getNewTestVideoNotifications, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Fetch course purchase notifications
      const courseRes = await axios.get(api.getCoursePurchaseNotifications, {
        headers: { Authorization: `Bearer ${token}` },
      });


      // Map test video notifications
      const videoNotifications = videoRes.data.newVideos.map((notif) => ({
        ...notif,
        type: 'video', // Add a type to distinguish
      }));

      // Map course purchase notifications
      const courseNotifications = courseRes.data.notifications.map((notif) => ({
        ...notif,
        type: 'course', // Add a type to distinguish
      }));

      // Combine and sort notifications by createdAt
      const combinedNotifications = [...courseNotifications, ...videoNotifications].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );

      setNotifications(combinedNotifications);
    } catch (err) {
      setError('Failed to fetch notifications.');
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();

    if (!socket) {
      // console.log('Socket is not available in InstructorNotificationPage');
      return;
    }
    // console.log('Setting up receiveNotification handler in InstructorNotificationPage');
    const handleReceiveNotification = (newNotification) => {
      // console.log('Received real-time instructor notification:', newNotification);
      setNotifications((prev) => [newNotification, ...prev]);
    };
    socket.on('receiveNotification', handleReceiveNotification);
    return () => {
      socket.off('receiveNotification', handleReceiveNotification);
      // console.log('Cleaned up receiveNotification handler in InstructorNotificationPage');
    };
  }, [fetchNotifications, socket]);

  const markAsRead = async (id, type) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      if (type === 'course') {
        await axios.put(api.markCoursePurchaseNotificationAsRead(id), {}, { headers });
      } else if (type === 'video') {
        await axios.put(api.markTestVideoAsRead(id), {}, { headers });
      }

      // Remove the notification from the list
      setNotifications((prev) => prev.filter((notif) => notif._id !== id));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      // Mark the notification as read
      await markAsRead(notif._id, notif.type);

      // Navigate based on notification type
      if (notif.type === 'course') {
        navigate('/dashboard');
      } else if (notif.type === 'video') {
        navigate(`/test-video/${notif._id}`);
      }
    } catch (err) {
      console.error('Failed to handle notification click:', err);
    }
  };

  if (loading) {
    return (
      <div className="instructor-notification-container">
        <div className="no-notifications">Loading notifications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="instructor-notification-container">
        <div className="no-notifications">{error}</div>
      </div>
    );
  }

  return (
    <div className="instructor-notification-container">
      {/* Close Button */}
      <button
        className="insta-notification-close-button"
        onClick={() => navigate(-1)} // Navigate back to the previous page
        aria-label="Close"
      >
        ✖
      </button>

      <h1>Instructor Notifications</h1>
      {notifications.length === 0 ? (
        <div className="no-notifications">No new notifications</div>
      ) : (
        notifications.map((notif) => (
          <div
            key={notif._id}
            className="notification-card"
            onClick={() => handleNotificationClick(notif)}
          >
            <div>
              {notif.type === 'course' ? (
                <p>
                  <strong>Course Purchase:</strong> {notif.userId ? notif.userId.name : 'Unknown User'} has purchased your course{' '}
                  <strong>{notif.courseId ? notif.courseId.title : 'Course Deleted'}</strong>
                </p>
              ) : notif.type === 'video' ? (
                <>
                  <p>
                    <strong>Test Video:</strong> Your video is{' '}
                    <strong>{notif.status && notif.status.toUpperCase()}</strong>
                  </p>
                  <p>Admin Comment: {notif.adminComment}</p>
                </>
              ) : (
                <p>{notif.message || notif.content}</p>
              )}
              <small>{new Date(notif.createdAt || notif.date).toLocaleString()}</small>
            </div>

            <button
              className="insta-mark-read-btn"
              onClick={(e) => {
                e.stopPropagation(); // Prevent navigation on button click
                markAsRead(notif._id, notif.type);
              }}
              aria-label="Delete Notification"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))
      )}
    </div>
  );
};

export default InstructorNotificationPage;
